import axios from 'axios';
import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { getToken } from '@modules/DataManager';

import './autocomplete.css';

// Stessa base URL del resto del gestionale (cfr. DataManager). L'endpoint
// /api/autocomplete risponde con un ARRAY grezzo (non l'envelope { success, data })
// quindi non passa per `doGet`: GET autenticata diretta col token Firebase.
const AUTOCOMPLETE_URL = `${import.meta.env.VITE_BASE_URL}/api/autocomplete`;

/**
 * AddressAutocomplete
 * - Input "uncontrolled-friendly": il testo digitato vive in stato locale, così la
 *   digitazione NON forza il re-render del parent (es. la Timeline pesante della
 *   pagina Viaggio) ad ogni carattere. La propagazione verso il parent (`onChange`)
 *   è debounced e viene "flushata" su blur/selezione.
 * - Fetch debounced + autenticata (token Firebase), con cancellazione della
 *   richiesta in volo e guardia per sequenza contro le risposte "stale".
 * - Nessuna richiesta ridondante: i cambi di `value` esterni (selezione, swap,
 *   prefill) non riaprono il dropdown né rifanno una query.
 * - Navigazione tastiera (↑ ↓ Enter Esc), ARIA combobox/listbox, click-outside.
 */
export default function AddressAutocomplete({
  value,
  onChange, // (e: { target: { name, value } }) => void
  onSelect, // (label: string, lat: number, lng: number) => void
  onBlur,
  onKeyDown,
  placeholder = '',
  label = '',
  id: idProp,
  name,
  disabled = false,
  readOnly = false,
  minChars = 3,
  debounceMs = 300,
  propagateMs = 250,
  apiUrl = AUTOCOMPLETE_URL,
}) {
  const autoId = useId();
  const inputId = idProp ?? autoId;
  const listboxId = `${inputId}-listbox`;

  const [inputValue, setInputValue] = useState(value ?? '');
  // Traccia l'ultimo `value` ricevuto dal parent per allineare lo stato locale
  // quando cambia dall'esterno (selezione/swap/prefill) — pattern ufficiale React
  // "adjusting state on prop change" via state, niente effetto né ref.
  const [lastValue, setLastValue] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);

  const cancelRef = useRef(null); // axios cancel token source
  const fetchTimerRef = useRef(null); // debounce fetch
  const emitTimerRef = useRef(null); // debounce onChange upward
  const reqIdRef = useRef(0); // guardia anti risposte "stale"
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);

  if (value !== lastValue) {
    setLastValue(value);
    setInputValue(value ?? '');
  }

  // Fetch debounced + autenticata. Lanciata SOLO dalla digitazione utente, mai dai
  // sync esterni → nessuna query/dropdown ridondante. La richiesta precedente viene
  // annullata; in più la guardia per sequenza scarta risposte arrivate fuori ordine.
  const runFetch = useCallback(
    (query) => {
      if (fetchTimerRef.current) window.clearTimeout(fetchTimerRef.current);
      fetchTimerRef.current = window.setTimeout(async () => {
        if (cancelRef.current) cancelRef.current.cancel('New request started');
        cancelRef.current = axios.CancelToken.source();
        const reqId = ++reqIdRef.current;
        try {
          const token = await getToken();
          const res = await axios.get(`${apiUrl}?${new URLSearchParams({ q: query })}`, {
            cancelToken: cancelRef.current.token,
            headers: { Authorization: `Bearer ${token}` },
          });
          if (reqId !== reqIdRef.current) return; // risposta superata
          // L'endpoint risponde con array grezzo; tolleriamo anche un eventuale envelope.
          const payload = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
          if (res.status === 200 && Array.isArray(payload) && payload.length > 0) {
            setSuggestions(payload);
            setOpen(true);
            setHighlighted(-1);
          } else {
            setSuggestions([]);
            setOpen(false);
          }
        } catch (err) {
          if (axios.isCancel(err) || reqId !== reqIdRef.current) return;
          setSuggestions([]);
          setOpen(false);
        }
      }, debounceMs);
    },
    [apiUrl, debounceMs]
  );

  // Cleanup su unmount: annulla la richiesta in volo e ferma i timer in sospeso.
  useEffect(() => {
    const cancel = () => cancelRef.current?.cancel('Component unmounted');
    return () => {
      cancel();
      if (fetchTimerRef.current) window.clearTimeout(fetchTimerRef.current);
      if (emitTimerRef.current) window.clearTimeout(emitTimerRef.current);
    };
  }, []);

  // Chiusura su click esterno (il menu vive in un portal → controlla anche menuRef).
  useEffect(() => {
    function handleDocMouseDown(e) {
      const inRoot = rootRef.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      if (!inRoot && !inMenu) setOpen(false);
    }
    document.addEventListener('mousedown', handleDocMouseDown);
    return () => document.removeEventListener('mousedown', handleDocMouseDown);
  }, []);

  // Il dropdown è renderizzato in un portal su <body> con position:fixed, così
  // nessun antenato (card con overflow, modali, stacking context) può tagliarlo —
  // stesso approccio di AppSelect (menuPortalTarget). Posizione ancorata all'input.
  const updateMenuPosition = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMenuStyle({ position: 'fixed', top: r.bottom, left: r.left, width: r.width });
  }, []);

  useLayoutEffect(() => {
    if (open && suggestions.length > 0) updateMenuPosition();
  }, [open, suggestions, inputValue, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onReflow = () => updateMenuPosition();
    window.addEventListener('scroll', onReflow, true); // cattura anche scroll di container interni
    window.addEventListener('resize', onReflow);
    return () => {
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [open, updateMenuPosition]);

  // Propagazione debounced verso il parent (evita il cascading re-render per carattere).
  const emitChange = useCallback(
    (val) => {
      if (emitTimerRef.current) window.clearTimeout(emitTimerRef.current);
      emitTimerRef.current = window.setTimeout(() => {
        onChange?.({ target: { name, value: val } });
      }, propagateMs);
    },
    [onChange, name, propagateMs]
  );

  const flushChange = useCallback(
    (val) => {
      if (emitTimerRef.current) {
        window.clearTimeout(emitTimerRef.current);
        emitTimerRef.current = null;
      }
      onChange?.({ target: { name, value: val } });
    },
    [onChange, name]
  );

  const handleChange = useCallback(
    (e) => {
      const v = e.target.value;
      setInputValue(v);
      emitChange(v);
      const q = v.trim();
      if (q.length < minChars) {
        if (fetchTimerRef.current) window.clearTimeout(fetchTimerRef.current);
        setSuggestions([]);
        setOpen(false);
      } else {
        runFetch(q);
      }
    },
    [emitChange, minChars, runFetch]
  );

  const choose = useCallback(
    (sugg) => {
      // shape attesa: { formatted_address, geometry: { location: { lat, lng } } }
      const label = sugg?.formatted_address ?? '';
      const lat = sugg?.geometry?.location?.lat;
      const lng = sugg?.geometry?.location?.lng;
      if (fetchTimerRef.current) window.clearTimeout(fetchTimerRef.current);
      if (emitTimerRef.current) {
        window.clearTimeout(emitTimerRef.current);
        emitTimerRef.current = null;
      }
      setInputValue(label);
      setOpen(false);
      setSuggestions([]);
      setHighlighted(-1);
      onSelect?.(label, lat, lng);
    },
    [onSelect]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (open && suggestions.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setHighlighted((i) => (i + 1) % suggestions.length);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setHighlighted((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
          return;
        }
        if (e.key === 'Enter' && highlighted >= 0 && highlighted < suggestions.length) {
          e.preventDefault();
          choose(suggestions[highlighted]);
          return;
        }
        if (e.key === 'Escape') {
          setOpen(false);
          return;
        }
      }
      onKeyDown?.(e);
    },
    [open, suggestions, highlighted, choose, onKeyDown]
  );

  const handleBlur = useCallback(
    (e) => {
      setOpen(false);
      flushChange(inputValue); // assicura il valore finale nel parent
      onBlur?.(e);
    },
    [flushChange, inputValue, onBlur]
  );

  return (
    <div ref={rootRef} className="form-group app-autocomplete">
      {label ? (
        <label htmlFor={inputId} className="app-input__label">
          {label}
        </label>
      ) : null}

      <input
        ref={inputRef}
        id={inputId}
        name={name}
        type="text"
        className="form-control app-input"
        placeholder={placeholder}
        autoComplete="off"
        readOnly={readOnly}
        disabled={disabled}
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={() => suggestions.length && setOpen(true)}
        aria-autocomplete="list"
        aria-controls={listboxId}
        aria-expanded={open}
        aria-activedescendant={highlighted >= 0 ? `${inputId}-option-${highlighted}` : undefined}
        role="combobox"
      />

      {open &&
        suggestions.length > 0 &&
        menuStyle &&
        createPortal(
          <ul
            ref={menuRef}
            id={listboxId}
            role="listbox"
            className="app-autocomplete-menu suggestions"
            style={menuStyle}
          >
            {suggestions.map((s, idx) => {
              const lat = s?.geometry?.location?.lat;
              const lng = s?.geometry?.location?.lng;
              const isActive = idx === highlighted;
              return (
                <li
                  id={`${inputId}-option-${idx}`}
                  key={`sugg-${idx}`}
                  role="option"
                  aria-selected={isActive}
                  className={isActive ? 'is-active' : undefined}
                  onMouseDown={(e) => {
                    // mousedown così il blur dell'input non chiude la lista prima del click
                    e.preventDefault();
                    choose(s);
                  }}
                >
                  {s?.formatted_address ?? `${lat}, ${lng}`}
                </li>
              );
            })}
          </ul>,
          document.body
        )}
    </div>
  );
}
